/**
 * app-search — types + validator for the global `_app` layout search state.
 *
 * Centralizes in the URL all overlays mounted at the `_app` level (command
 * palette and global dialogs of `AppLayoutDialogs`), so that pasting the
 * URL in another browser reopens the exact same screen/dialog.
 *
 * Pattern (same as #9 `/?q=`): `validateSearch` omits defaults (so it does not
 * pollute the URL in empty state) and `AppLayoutContent` reflects URL→signals + navigates with
 * `{ replace: true }` on open/close (idempotent, survives back/forward and
 * a pasted URL).
 */

/** Identity of open global dialog (one at a time). */
export type AppDialogKind =
  | "choice"
  | "list-new"
  | "list-edit"
  | "group-new"
  | "group-edit"
  | "share";

/** Modal with no entity (shortcut reference). */
export type AppModalKind = "shortcuts";

export interface AppLayoutSearch {
  // ── Command palette ────────────────────────────────────────────────────────
  /** Palette open. */
  palette?: true;
  /** Query typed into palette (copyable). */
  pq?: string;
  /** Palette filter (all = default omitted). */
  pf?: "todo" | "list";

  // ── Global dialogs (AppLayoutDialogs) ────────────────────────────────────
  /** Open global dialog. */
  dialog?: AppDialogKind;
  /** List id for `list-edit` / `share`. */
  listId?: string;
  /** Group id for `group-edit`. */
  groupId?: string;
  /** Open modal with no entity. */
  modal?: AppModalKind;

  // ── Task detail (drawer) — sub-states ─────────────────────────────────
  /** Schedule panel (date/reminder/recurrence) open in detail. */
  sched?: true;

  // ── Task list view — view state ──────────────────────────────
  /** "Completed" section expanded (default collapsed → omitted). */
  done?: true;

  // ── Account switch (multi-account #18) ─────────────────────────────────────
  /** Account switcher open (`?account=switch`). */
  account?: "switch";
}

const DIALOG_KINDS = new Set<AppDialogKind>([
  "choice",
  "list-new",
  "list-edit",
  "group-new",
  "group-edit",
  "share",
]);

function str(v: unknown): string | undefined {
  return typeof v === "string" && v.trim() !== "" ? v : undefined;
}

// Tolerant boolean coercion for URL flags. CC-18: TanStack's default search
// parser auto-parses `?palette=1` into NUMBER `1` (not string
// "1"), so without `=== 1` the flag was discarded and the router WIPED the param
// during boot → a pasted deep-link `?palette=1` failed to open the overlay.
function truthyFlag(v: unknown): boolean {
  return v === true || v === 1 || v === "1" || v === "true";
}

// Type-guard: narrows a string to AppDialogKind without unsafe assertion. The cast to
// ReadonlySet<string> only WIDENS the Set type (safe), does not narrow value.
function isDialogKind(v: string): v is AppDialogKind {
  return (DIALOG_KINDS as ReadonlySet<string>).has(v);
}

/**
 * Validates + normalizes the layout search. Omits defaults to keep the URL
 * clean. Tolerant: unknown values are discarded (rendering does not break).
 */
export function validateAppLayoutSearch(search: Record<string, unknown>): AppLayoutSearch {
  const out: AppLayoutSearch = {};

  // ── Palette ──
  if (truthyFlag(search.palette)) {
    out.palette = true;
  }
  const pq = str(search.pq);
  if (pq) {
    out.pq = pq;
    // A query implies open palette even if flag is missing (shorter URL).
    out.palette = true;
  }
  if (search.pf === "todo" || search.pf === "list") {
    out.pf = search.pf;
    out.palette = true;
  }

  // ── Global dialogs ──
  const dialog = str(search.dialog);
  if (dialog && isDialogKind(dialog)) {
    out.dialog = dialog;
    const listId = str(search.listId);
    const groupId = str(search.groupId);
    if (listId) {
      out.listId = listId;
    }
    if (groupId) {
      out.groupId = groupId;
    }
  }

  // ── Modal with no entity ──
  if (search.modal === "shortcuts") {
    out.modal = "shortcuts";
  }

  // ── Detail: schedule panel ──
  if (truthyFlag(search.sched)) {
    out.sched = true;
  }

  // ── List view: completed expanded ──
  if (truthyFlag(search.done)) {
    out.done = true;
  }

  // ── Account switcher (multi-account) ──
  if (search.account === "switch") {
    out.account = "switch";
  }

  return out;
}
